import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import Sort from './Sort';
import Stops from './filters/Stops';
import FlightDuration from './filters/FlightDuration';
import BoardType from './filters/BoardType';
import Facilities from './filters/Facilities';
import Nights from './filters/Nights';
import Refundability from './filters/Refundability';
import BaggageRate from './filters/BaggageRate';
import MobileSecondaryFilter from './MobileSecondaryFilter';
import translate from '../../utilities/translator';
import { selectIsSecondaryFilterClear, selectSecondaryFilter } from './secondaryFilterSlice';
import StarsFilter from './filters/StarsFilter';
import SearchByHotel from './filters/SearchByHotel';
import { mobileSearchLabelSelector } from '../../services/selectors';
import { isMobile } from '../../utilities/helpers';
import FadeTransition from '../../components/animations/FadeTransition';
import { pushEvent } from '../../utilities/analytics';
import { SEC_FILTER_OPEN } from '../../constants/analyticEvents';
import { createPortal } from 'react-dom';
import useHideFilter from '../../hooks/useHideFilter';
import { updateSecondaryFilterModal } from './secondaryFilterModalsSlice';
import { selectGlobalLoading } from '../../appSlice';
import abTesting from '../../services/abTesting';

const SecondaryFilter = ({ sticky }) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const isFilterClear = useSelector(selectIsSecondaryFilterClear);
  const selectedHotel = useSelector((state) => state.userSearch.selectedHotel);
  const searchLabel = useSelector(mobileSearchLabelSelector);
  const { mobile: secondaryFilterMobile } = useSelector((state) => state.modals.secondaryFilter);
  const [mobileOpen, setMobileOpen] = useState(secondaryFilterMobile);
  const isRtl = document.querySelector('html').getAttribute('dir') === 'rtl';
  const { pending, error } = secondaryFilter;
  const { secondaryFilter: secondaryFilterModals } = useSelector((state) => state.modals);
  const { secondaryFilterLoading } = useSelector(selectGlobalLoading);
  const { stars, sort, board, stops, facilities, duration, nights, refundability } = secondaryFilterModals;

  const visible = useHideFilter();
  const cls = visible ? 'visible-secondary-filters' : 'hidden-filters';

  const [externalUpdatesArr, setExternalUpdatesArr] = useState([]);

  let externalUpdatesArrTemp = [];
  const addFilterToSet = (func) => {
    externalUpdatesArrTemp = [...externalUpdatesArrTemp, func];
    setExternalUpdatesArr(externalUpdatesArrTemp);
  };

  window.external_set_secondary_filter = () => {
    console.log('Set secondary filters');
    externalUpdatesArr.forEach((func) => func());
    externalUpdatesArrTemp = [];
    setExternalUpdatesArr([]);
  };

  useEffect(() => {
    setMobileOpen(secondaryFilterMobile);
  }, [secondaryFilterMobile]);

  const handlePopup = () => {
    if (!mobileOpen) {
      pushEvent(SEC_FILTER_OPEN, { filterName: 'mobile' });
    }
    window.history.pushState({ type: 'something' }, null, window.location.href);
    setMobileOpen(!mobileOpen);
    setTimeout(() => {
      dispatch(updateSecondaryFilterModal({ mobile: !mobileOpen }));
    }, 0);
  };

  const handleShowModals = (isOpen, filterName) => {
    dispatch(updateSecondaryFilterModal({ [filterName]: isOpen }));
  };

  useEffect(() => {
    let popstateListener = () => {
      if (window.innerWidth < 1200) {
        dispatch(updateSecondaryFilterModal({ mobile: false }));
      }
    };

    window.addEventListener('popstate', popstateListener);

    return () => {
      window.removeEventListener('popstate', popstateListener);
    };
  }, [dispatch]);

  if (secondaryFilterLoading) {
    return (
      <div className="secondary-filter">
        <div className="secondary-filter-content loader">
          <div className="secondary-filter-loader" />
          <div className="secondary-filter-loader" />
          <div className="secondary-filter-loader" />
          <div className="secondary-filter-loader" />
          <div className="secondary-filter-loader" />
          <div className="secondary-filter-loader" />
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="secondary-filter">Some error. Try to reload the page.</div>;
  }

  const content = (
    <div ref={ref} className="secondary-filter">
      <div
        className={classNames(`secondary-filter-content ${cls}`, {
          'secondary-filter-content--sticky': sticky,
          pending: pending,
          'sfc-with-multi-rate': abTesting.moveMultiRates
        })}
      >
        <div className="c-container">
          <div className="c-row">
            <div className="col-lg-12 display-flex">
              <div className="secondary-filter__wrap">
                <SearchByHotel />
                {abTesting.moveMultiRates &&
                  <BaggageRate
                    side={isRtl ? 'right' : 'left'}
                    setIsOpen={(isOpen) => handleShowModals(isOpen, 'luggage')}
                  />
                }
                {abTesting.moveMultiRates &&
                  <Refundability
                    side={isRtl ? 'right' : 'left'}
                    isOpen={refundability}
                    setIsOpen={(isOpen) => handleShowModals(isOpen, 'refundability')}
                  />}
                <StarsFilter
                  side={isRtl ? 'right' : 'left'}
                  isOpen={stars}
                  setIsOpen={(isOpen) => handleShowModals(isOpen, 'stars')}
                  addFilterToSet={addFilterToSet}
                />
                <Nights
                  side={isRtl ? 'right' : 'left'}
                  isOpen={nights}
                  setIsOpen={(isOpen) => handleShowModals(isOpen, 'nights')}
                  addFilterToSet={addFilterToSet}
                />
                <Stops
                  isOpen={stops}
                  setIsOpen={(isOpen) => handleShowModals(isOpen, 'stops')}
                  addFilterToSet={addFilterToSet}
                />
                <BoardType
                  isOpen={board}
                  setIsOpen={(isOpen) => handleShowModals(isOpen, 'board')}
                  addFilterToSet={addFilterToSet}
                />
                <Facilities
                  side={isRtl ? 'left' : 'right'}
                  isOpen={facilities}
                  setIsOpen={(isOpen) => handleShowModals(isOpen, 'facilities')}
                  addFilterToSet={addFilterToSet}
                />
                <FlightDuration
                  isOpen={duration}
                  setIsOpen={(isOpen) => handleShowModals(isOpen, 'duration')}
                  addFilterToSet={addFilterToSet}
                />
              </div>
              <Sort
                isOpen={sort}
                setIsOpen={(isOpen) => handleShowModals(isOpen, 'sort')}
                addFilterToSet={addFilterToSet}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="c-row">
      <div
        style={{
          position: 'relative',
          height: sticky ? (isMobile() ? '66px' : '40px') : 'auto',
        }}
      />

      {sticky ? createPortal(content, document.querySelector('.main-filter__outer-wrap')) : content}
      <FadeTransition in={mobileOpen} unmountOnExit>
        <MobileSecondaryFilter />
      </FadeTransition>

      <div
        ref={mobileRef}
        className={classNames(`col mobile-filter-panel notBigDesktop ${cls}`, {
          sticky,
        })}
      >
        <div className="filters">
          <SearchByHotel />
          <div
            onClick={handlePopup}
            className={classNames('secondary-filter-popup__open-icon', {
              'secondary-filter--active': !isFilterClear,
            })}
          >
            {translate('holidayFinder.secondaryFilter.filters')}
            <i className="ri-add-line" />
          </div>
        </div>
        {(!isFilterClear || selectedHotel.hotelId) && (
          <div className="mobile-search-label notBigDesktop">{searchLabel}</div>
        )}
      </div>
    </div>
  );
};

export default SecondaryFilter;
